import type { ReactNode } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';

interface AlertProps {
  children: ReactNode;
  variant?: 'success' | 'error' | 'info' | 'warning';
  title?: string;
  dismissible?: boolean;
  onDismiss?: () => void;
  className?: string;
}

const Alert = ({ 
  children, 
  variant = 'info', 
  title, 
  dismissible = false, 
  onDismiss, 
  className = '' 
}: AlertProps) => {
  const baseClasses = 'rounded-lg border p-4 relative';

  const variantClasses = {
    success: 'border-oro-success/40 bg-oro-success/10 text-oro-success',
    error: 'border-oro-error/40 bg-oro-error/10 text-oro-error',
    info: 'border-oro-gold/40 bg-oro-gold/10 text-oro-gold-text',
    warning: 'border-oro-warning/40 bg-oro-warning/10 text-oro-warning'
  };

  const titleClasses = {
    success: 'text-oro-success',
    error: 'text-oro-error',
    info: 'text-oro-gold',
    warning: 'text-oro-warning'
  };

  const iconClasses = {
    success: 'text-oro-success',
    error: 'text-oro-error',
    info: 'text-oro-gold',
    warning: 'text-oro-warning'
  };

  const getIcon = (variant: string) => {
    switch (variant) {
      case 'success':
        return <CheckCircle className="w-5 h-5" />;
      case 'error':
        return <XCircle className="w-5 h-5" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5" />;
      case 'info':
      default:
        return <Info className="w-5 h-5" />;
    }
  };

  const classes = `${baseClasses} ${variantClasses[variant]} ${className}`;

  return (
    <div className={classes} role="alert">
      <div className="flex items-start gap-3">
        <span className={`${iconClasses[variant]} shrink-0 mt-0.5`}>
          {getIcon(variant)}
        </span>
        <div className="flex-1 min-w-0">
          {title && (
            <div className={`text-sm font-semibold mb-1 ${titleClasses[variant]}`}>
              {title}
            </div>
          )}
          <div className="text-sm">
            {children}
          </div>
        </div>
        {dismissible && onDismiss && (
          <button
            onClick={onDismiss}
            className={`${iconClasses[variant]} hover:opacity-70 shrink-0`}
            aria-label="Dismiss alert"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default Alert;